import React, { createContext, useContext, useState, useEffect } from 'react';

type ThemeType = 'default' | 'dark' | 'sepia' | 'ocean' | 'forest';

interface ThemeContextType {
  theme: ThemeType;
  setTheme: (theme: ThemeType) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const themes = {
  default: {
    name: 'Default',
    primary: 'bg-gradient-to-r from-blue-600 to-indigo-600',
    primaryHover: 'hover:bg-gradient-to-r hover:from-blue-700 hover:to-indigo-700',
    secondary: 'bg-gray-50/80',
    secondaryHover: 'hover:bg-gray-100/90',
    text: 'text-gray-900',
    textSecondary: 'text-gray-600',
    background: 'bg-gradient-to-br from-blue-50 to-indigo-50',
    card: 'bg-white/90 backdrop-blur-sm',
    border: 'border-gray-200',
    highlight: 'bg-blue-50 text-blue-600',
    icon: 'text-blue-600',
    dropdown: 'bg-white/95 backdrop-blur-sm'
  },
  dark: {
    name: 'Dark Mode',
    primary: 'bg-gradient-to-r from-indigo-600 to-purple-600',
    primaryHover: 'hover:bg-gradient-to-r hover:from-indigo-700 hover:to-purple-700',
    secondary: 'bg-gray-800/80',
    secondaryHover: 'hover:bg-gray-700/90',
    text: 'text-gray-100',
    textSecondary: 'text-gray-300',
    background: 'bg-gradient-to-br from-gray-900 to-indigo-900',
    card: 'bg-gray-800/90 backdrop-blur-sm',
    border: 'border-gray-700',
    highlight: 'bg-indigo-900 text-indigo-200',
    icon: 'text-indigo-400',
    dropdown: 'bg-gray-800/95 backdrop-blur-sm'
  },
  sepia: {
    name: 'Sepia',
    primary: 'bg-gradient-to-r from-amber-700 to-orange-600',
    primaryHover: 'hover:bg-gradient-to-r hover:from-amber-800 hover:to-orange-700',
    secondary: 'bg-amber-100/80',
    secondaryHover: 'hover:bg-amber-200/90',
    text: 'text-amber-900',
    textSecondary: 'text-amber-700',
    background: 'bg-gradient-to-br from-amber-50 to-orange-50',
    card: 'bg-amber-100/90 backdrop-blur-sm',
    border: 'border-amber-200',
    highlight: 'bg-amber-100 text-amber-800',
    icon: 'text-amber-700',
    dropdown: 'bg-amber-100/95 backdrop-blur-sm'
  },
  ocean: {
    name: 'Ocean',
    primary: 'bg-gradient-to-r from-cyan-600 to-blue-500',
    primaryHover: 'hover:bg-gradient-to-r hover:from-cyan-700 hover:to-blue-600',
    secondary: 'bg-cyan-100/80',
    secondaryHover: 'hover:bg-cyan-200/90',
    text: 'text-slate-800',
    textSecondary: 'text-slate-600',
    background: 'bg-gradient-to-br from-cyan-50 to-blue-50',
    card: 'bg-white/90 backdrop-blur-sm',
    border: 'border-cyan-200',
    highlight: 'bg-cyan-100 text-cyan-700',
    icon: 'text-cyan-600',
    dropdown: 'bg-white/95 backdrop-blur-sm'
  },
  forest: {
    name: 'Forest',
    primary: 'bg-gradient-to-r from-emerald-600 to-green-500',
    primaryHover: 'hover:bg-gradient-to-r hover:from-emerald-700 hover:to-green-600',
    secondary: 'bg-emerald-100/80',
    secondaryHover: 'hover:bg-emerald-200/90',
    text: 'text-emerald-900',
    textSecondary: 'text-emerald-700',
    background: 'bg-gradient-to-br from-emerald-50 to-green-50',
    card: 'bg-white/90 backdrop-blur-sm',
    border: 'border-emerald-200',
    highlight: 'bg-emerald-100 text-emerald-700',
    icon: 'text-emerald-600',
    dropdown: 'bg-white/95 backdrop-blur-sm'
  }
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeType>('default');

  useEffect(() => {
    const savedTheme = localStorage.getItem('bookBlissTheme') as ThemeType;
    if (savedTheme && Object.keys(themes).includes(savedTheme)) {
      setTheme(savedTheme);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('bookBlissTheme', theme);
    document.body.className = themes[theme].background;
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const getThemeClasses = (theme: ThemeType) => {
  return themes[theme];
};